import {
  Clock,
  Euler,
  PerspectiveCamera,
  Scene,
  Vector2,
  WebGLRenderer,
} from "three";
import { Stars } from "./objects/stars";
import { Style } from "./style";
import { addEase, addEase2 } from "../utils/ease";

export class Renderer {
  private style: Style;

  private renderer: WebGLRenderer;
  private camera: PerspectiveCamera;
  private scene: Scene;
  private canvas: HTMLElement;

  private initialZPosition = 1200;
  private initialRotation = Math.PI * 2;
  private rotateToY = this.initialRotation;
  private moveToZ = this.initialZPosition;
  private moveToX = 0;
  private mouse: Vector2;
  private mouseSmooth: Vector2;
  private width = 0;
  private height = 0;

  private clock = new Clock();
  private delta = 0;
  private interval = 1 / 60;

  private stars: Stars;

  constructor(style: Style) {
    this.style = style;
    this.canvas = document.getElementById("mainCanvas")!;
    this.width = this.canvas.offsetWidth;
    this.height = this.canvas.offsetHeight;

    document.querySelector("#logo")?.addEventListener("click", (event) => {
      event.preventDefault();
      this.setSpeed();
    });

    this.mouse = new Vector2(this.width / 2, this.height / 2);
    this.mouseSmooth = this.mouse;

    this.renderer = new WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    this.renderer.setSize(this.width, this.height);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.autoClear = false;
    this.renderer.setClearColor(0x000000, 0.0);

    this.canvas?.appendChild(this.renderer.domElement);

    this.camera = new PerspectiveCamera(
      this.style.cameraPerspective,
      this.width / this.height,
      0.1,
      12000,
    );

    this.scene = new Scene();
    this.scene.add(this.camera);
    this.scene.translateZ(this.initialZPosition);

    this.stars = new Stars();
    this.scene.add(this.stars);

    this.render();
  }

  public setSpeed(): void {
    this.stars.setSpeed(20);
  }

  public resize(): void {
    if (
      this.width != this.canvas.offsetWidth ||
      this.height != this.canvas.offsetHeight
    ) {
      this.width = this.canvas.offsetWidth;
      this.height = this.canvas.offsetHeight;
      this.renderer.setSize(this.width, this.height);
      this.camera.aspect = this.width / this.height;
      this.camera.updateProjectionMatrix();
    }
  }

  public setMouse(mouseX: number, mouseY: number): void {
    if (mouseX && mouseY) {
      this.mouse = new Vector2(mouseX, mouseY);
    }
  }

  public setScroll(scrollPercent: number): void {
    this.moveToZ =
      this.initialZPosition - (scrollPercent * this.initialZPosition) / 100;
    this.rotateToY =
      this.initialRotation - (scrollPercent * 180 * Math.PI) / 18000;
    this.moveToX =
      scrollPercent < 50 ? scrollPercent * -12 : -1200 + scrollPercent * 12;
  }

  public destroy(): void {
    this.renderer.forceContextLoss();
  }

  private update(): void {
    addEase2(this.mouseSmooth, this.mouse, 12);
    const move = addEase(this.camera.position.z, this.moveToZ, 12);
    this.camera.position.set(
      this.width / 2 + this.mouseSmooth.x / 8 - this.width / 2 + this.moveToX,
      0,
      move,
    );
    this.camera.setRotationFromEuler(new Euler(0, this.rotateToY, 0));
    this.stars.update();
  }

  private render(): void {
    requestAnimationFrame(() => this.render());
    this.delta += this.clock.getDelta();
    if (this.delta > this.interval) {
      this.update();
      this.renderer.clear();
      this.renderer.render(this.scene, this.camera);
      this.delta = this.delta % this.interval;
    }
  }
}
